import { useState, useMemo } from 'react';
import { resolveCreditBalance } from '../services/api';
import './ChangePayerModal.css';

// Resolution methods, in the order a biller is most likely to reach for them.
const METHODS = [
  { value: 'recoupment',      label: 'Recoupment',     blurb: 'Payer took the money back (or will).', wantsRef: true },
  { value: 'refund',          label: 'Refund sent',    blurb: 'You sent a check/EFT back to the payer.', wantsRef: true },
  { value: 'applied_to_claim',label: 'Applied to claim', blurb: 'Credit applied to another open balance.', wantsRef: false },
  { value: 'write_off',       label: 'Write off',      blurb: 'Close out the balance without repaying.', wantsRef: false },
];

// Terminal status each method lands on when the FULL open balance is resolved.
const FULL_STATUS_LABEL = {
  recoupment:       'Recouped',
  refund:           'Refund Sent',
  applied_to_claim: 'Resolved',
  write_off:        'Written Off',
};

function fmtCurrency(n) {
  return '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Record a refund / recoupment / write-off against an open credit balance.
 * Supports PARTIAL recovery: the amount defaults to the full open balance but is
 * editable, so a biller can log (say) a $40 takeback against a $150 credit and
 * leave it Partially Recouped. Over-application is blocked, not silently capped.
 */
export default function ResolveCreditBalanceModal({ creditBalance: cb, onClose, onResolved }) {
  const openBalance = parseFloat(cb.open_balance) || 0;

  const [method, setMethod]       = useState('recoupment');
  const [amount, setAmount]       = useState(openBalance.toFixed(2));
  const [reference, setReference] = useState('');
  const [notes, setNotes]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState(null);

  const wantsRef = METHODS.find((m) => m.value === method)?.wantsRef;
  const amt = parseFloat(amount);

  // Validation + a live preview of the resulting status so there are no surprises.
  const { valid, validationMsg, resultPreview } = useMemo(() => {
    if (amount === '' || isNaN(amt)) return { valid: false, validationMsg: null, resultPreview: null };
    if (amt <= 0) return { valid: false, validationMsg: 'Enter an amount greater than $0.', resultPreview: null };
    if (amt > openBalance + 0.005) {
      return {
        valid: false,
        validationMsg: `Amount can't exceed the open balance of ${fmtCurrency(openBalance)}.`,
        resultPreview: null,
      };
    }
    const fully = amt >= openBalance - 0.005;
    const remaining = Math.max(0, openBalance - amt);
    return {
      valid: true,
      validationMsg: null,
      resultPreview: fully
        ? `Fully resolves this credit → status “${FULL_STATUS_LABEL[method]}”.`
        : `Partial — ${fmtCurrency(amt)} of ${fmtCurrency(openBalance)} recovered, ${fmtCurrency(remaining)} left open → status “Partially Recouped”.`,
    };
  }, [amount, amt, openBalance, method]);

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    setError(null);
    try {
      await resolveCreditBalance(cb.credit_balance_id, {
        resolution_method: method,
        amount: amt,
        refund_reference: wantsRef ? (reference.trim() || null) : null,
        notes: notes.trim() || null,
      });
      onResolved?.();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to resolve the credit balance.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cpm-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div className="cpm-modal">
        <div className="cpm-header">
          <div>
            <h2 className="cpm-title">Resolve Credit Balance</h2>
            <p className="cpm-subtitle">
              {cb.claim_number} · {cb.patient_name}{cb.payer_name ? ` · ${cb.payer_name}` : ''}
            </p>
          </div>
          <button className="cpm-close" onClick={onClose} disabled={submitting}>×</button>
        </div>

        <div className="cpm-body">
          <div className="cpm-mech cpm-mech--rebill" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Open balance owed to payer</span>
            <strong>{fmtCurrency(openBalance)}</strong>
          </div>

          {/* Method picker */}
          <label className="cpm-field-label">Resolution method</label>
          <div className="cpm-full" style={{ marginBottom: 14 }}>
            {METHODS.map((m) => (
              <label key={m.value} className="cpm-radio">
                <input
                  type="radio"
                  name="resolve-method"
                  value={m.value}
                  checked={method === m.value}
                  onChange={() => setMethod(m.value)}
                />
                <span><strong>{m.label}</strong> — <span className="cpm-muted">{m.blurb}</span></span>
              </label>
            ))}
          </div>

          <div className="cpm-field-row">
            <div className="cpm-field">
              <label className="cpm-field-label">Amount this step</label>
              <input
                className="cpm-input"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="cpm-field" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="cpm-btn-secondary"
                style={{ padding: '8px 10px' }}
                onClick={() => setAmount(openBalance.toFixed(2))}
                disabled={submitting}
              >
                Full balance
              </button>
            </div>
          </div>

          {wantsRef && (
            <div className="cpm-field" style={{ marginTop: 12 }}>
              <label className="cpm-field-label">Reference (check #, EFT trace, or payer ICN) — optional</label>
              <input
                className="cpm-input"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="e.g. EFT 88123 / ICN 250…"
              />
            </div>
          )}

          <div className="cpm-field" style={{ marginTop: 12 }}>
            <label className="cpm-field-label">Notes (optional)</label>
            <input
              className="cpm-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Context for this resolution"
            />
          </div>

          {validationMsg && <p className="cpm-error">{validationMsg}</p>}
          {resultPreview && <p className="cpm-hint" style={{ marginTop: 10 }}>{resultPreview}</p>}
          {error && <p className="cpm-error">{error}</p>}

          <div className="cpm-actions">
            <button className="cpm-btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
            <button className="cpm-btn-primary" onClick={submit} disabled={!valid || submitting}>
              {submitting ? 'Saving…' : 'Record resolution'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
